import wellknown from "wellknown";

export interface LatLng {
  lat: number;
  lng: number;
}

const isValidLat = (n: number) => n >= -90 && n <= 90;
const isValidLng = (n: number) => n >= -180 && n <= 180;

/** Convert raw [x,y] or [x,y,z] to LatLng, auto-detecting (lon,lat) vs (lat,lon) order */
function toLatLng(coord: number[], trySwap = false): LatLng {
  const a = coord[0] ?? 0;
  const b = coord[1] ?? 0;
  if (trySwap) {
    return { lat: a, lng: b }; // (lat, lon) order
  }
  return { lat: b, lng: a }; // (lon, lat) order GeoJSON/WKT standard
}

/** Check if a path has valid geographic coordinates */
function pathIsValid(path: LatLng[]): boolean {
  return path.every((p) => isValidLat(p.lat) && isValidLng(p.lng));
}

/** Ensure ring is closed (first point = last point) */
function closeRing(path: LatLng[]): LatLng[] {
  if (path.length < 2) return path;
  const first = path[0];
  const last = path[path.length - 1];
  if (first.lat !== last.lat || first.lng !== last.lng) {
    return [...path, { ...first }];
  }
  return path;
}

/** Extract outer ring coords from raw GeoJSON structure, handling Z/M dimensions */
function extractRingCoords(ring: number[][]): number[][] {
  return ring.map((c) => (Array.isArray(c) ? [c[0], c[1]] : [0, 0]));
}

/** Convert ring to path with coordinate order auto-detection */
function ringToPath(ring: number[][]): LatLng[] | null {
  const coords = extractRingCoords(ring);
  if (coords.length < 3) return null;

  const asLonLat = coords.map((c) => toLatLng(c, false));
  const asLatLon = coords.map((c) => toLatLng(c, true));

  if (pathIsValid(asLonLat)) return closeRing(asLonLat);
  if (pathIsValid(asLatLon)) return closeRing(asLatLon);
  return closeRing(asLonLat); // fallback to standard order
}

/** Parse geometry to paths, handles Polygon, MultiPolygon, GeometryCollection */
function geometryToPaths(geom: any): LatLng[][] {
  if (!geom || !geom.coordinates) return [];

  if (geom.type === "Polygon") {
    const path = ringToPath(geom.coordinates[0] || []);
    return path && path.length >= 3 ? [path] : [];
  }

  if (geom.type === "MultiPolygon") {
    return (geom.coordinates || [])
      .map((p: number[][][]) => ringToPath(p?.[0] || []))
      .filter((p): p is LatLng[] => !!p && p.length >= 3);
  }

  if (geom.type === "GeometryCollection" && geom.geometries) {
    for (const g of geom.geometries) {
      const paths = geometryToPaths(g);
      if (paths.length > 0) return paths;
    }
  }

  return [];
}

/**
 * Parses polygon data from WKT or GeoJSON format into Google Maps path format.
 * Returns array of paths (for MultiPolygon) or single path (for Polygon).
 */
export function parsePolygonToPaths(
  input: string
): { paths: LatLng[][]; error?: string } {
  if (!input || typeof input !== "string") {
    return { paths: [] };
  }

  let trimmed = input
    .trim()
    .replace(/^\uFEFF/, "")
    .replace(/\s+/g, " ");
  if (!trimmed) return { paths: [] };

  try {
    // GeoJSON
    if (trimmed.startsWith("{")) {
      const geojson = JSON.parse(trimmed);
      let geometry =
        geojson.type === "Feature" ? geojson.geometry : geojson;

      if (geojson.type === "FeatureCollection" && geojson.features?.[0]) {
        const f = geojson.features[0];
        geometry = f.type === "Feature" ? f.geometry : f;
      }

      const paths = geometryToPaths(geometry);
      return paths.length > 0 ? { paths } : { paths: [], error: "Invalid GeoJSON: missing geometry" };
    }

    // WKT strip SRID prefix (handles SRID=4326;, SRID=;, etc.)
    const wkt =
      trimmed.includes(";") && /^SRID=/i.test(trimmed)
        ? trimmed.split(";").slice(1).join(";").trim() || trimmed
        : trimmed;

    let geoJsonData = wellknown(wkt);
    if (!geoJsonData) geoJsonData = wellknown(trimmed);

    if (!geoJsonData || !geoJsonData.coordinates) {
      return { paths: [], error: "Invalid WKT" };
    }

    const paths = geometryToPaths(geoJsonData);
    return paths.length > 0 ? { paths } : { paths: [], error: "WKT type not supported (use Polygon or MultiPolygon)" };
  } catch (e) {
    return {
      paths: [],
      error: e instanceof Error ? e.message : "Failed to parse polygon",
    };
  }
}

/**
 * Converts GeoJSON geometry/Feature/FeatureCollection to WKT string (for backend compatibility).
 */
export function geojsonToWkt(geojson: object): string {
  try {
    const gj = geojson as any;
    if (gj.type === "FeatureCollection" && gj.features?.[0]) {
      return wellknown.stringify(gj.features[0]);
    }
    return wellknown.stringify(gj);
  } catch {
    return "";
  }
}

/**
 * Detects if input is GeoJSON format.
 */
export function isGeoJson(input: string): boolean {
  const trimmed = (input || "").trim();
  return trimmed.startsWith("{");
}

/**
 * Normalizes polygon string the same way the parser does (trim, BOM, collapse whitespace).
 * Use when setting polygon from API so it matches what we get after paste.
 */
export function normalizePolygonString(input: string): string {
  if (!input || typeof input !== "string") return "";
  return input
    .trim()
    .replace(/^\uFEFF/, "")
    .replace(/\s+/g, " ");
}
